import { useEffect, useMemo, useState } from 'react'
import { getCaseMeshes, downloadCaseMesh } from '../services/api'
import { renderReport } from '../lib/meshRenderer'
import { buildIllustration } from '../lib/illustrationDoc'

/* global __APP_VERSION__ */
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'v1.0'

const VIEWS = [['A', 'Anterior'], ['P', 'Posterior'], ['LL', 'Left Lateral'], ['RL', 'Right Lateral'], ['S', 'Superior'], ['I', 'Inferior'], ['ISO', 'Isometric']]
const TONES = [['White', '#F2F1EE'], ['Bone', '#CFC1A7'], ['Light', '#F5EBCB']]

let _pid = 1
const emptyProduct = () => ({ id: _pid++, file: null, fileName: '', meshFileId: '', productName: '', productId: '', view: 'ISO', image: '' })

export default function PackagingPage() {
  const [mode, setMode] = useState('upload') // 'upload' | 'case' — same order as STL/OBJ to PDF
  const [header, setHeader] = useState({ surgeonName: '', hospitalName: '', patientName: '', caseId: '' })
  const setH = (k, v) => setHeader((p) => ({ ...p, [k]: v }))

  const [look, setLook] = useState({ boneColor: '#F2F1EE', shadingT: 0.5, roughness: 0.85, background: 'white' })
  const setL = (k, v) => setLook((p) => ({ ...p, [k]: v }))
  const [perspective, setPerspective] = useState(true) // product-shot look (fills the label); off → flat ortho

  const [meshes, setMeshes] = useState([])
  const [meshesLoading, setMeshesLoading] = useState(false)
  const [products, setProducts] = useState([emptyProduct()])
  const updP = (i, patch) => setProducts((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)))

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [logo, setLogo] = useState(null)
  const [logoAspect, setLogoAspect] = useState(3)

  useEffect(() => {
    fetch('/osteo3d-logo.png').then((r) => r.blob()).then((b) => {
      const fr = new FileReader()
      fr.onload = () => { setLogo(fr.result); const im = new Image(); im.onload = () => setLogoAspect(im.naturalWidth / im.naturalHeight || 3); im.src = fr.result }
      fr.readAsDataURL(b)
    }).catch(() => {})
  }, [])

  // switching source mode resets the product list
  function switchMode(m) { if (m === mode) return; setMode(m); setProducts([emptyProduct()]); setMeshes([]) }

  // Upload: one product per selected file (one PDF page each).
  function onUploadFiles(fileList) {
    const files = [...(fileList || [])]
    if (!files.length) return
    setProducts(files.map((f) => ({ id: _pid++, file: f, fileName: f.name, meshFileId: '', productName: f.name.replace(/\.[^.]+$/, ''), productId: '', view: 'ISO', image: '' })))
  }

  useEffect(() => {
    if (mode !== 'case' || header.caseId.length < 8) { setMeshes([]); return }
    setMeshesLoading(true)
    const t = setTimeout(() => {
      getCaseMeshes(header.caseId).then((r) => setMeshes(r.data.meshes || [])).catch(() => setMeshes([])).finally(() => setMeshesLoading(false))
    }, 450)
    return () => clearTimeout(t)
  }, [mode, header.caseId])

  async function bytesFor(p) {
    if (mode === 'upload') { if (!p.file) throw new Error('Choose files above'); return { bytes: await p.file.arrayBuffer(), filename: p.fileName } }
    if (!p.meshFileId) throw new Error('Pick a mesh for each product')
    const r = await downloadCaseMesh(header.caseId, p.meshFileId)
    return { bytes: r.data, filename: meshes.find((m) => m.id === p.meshFileId)?.name }
  }

  async function generate() {
    setError(''); setBusy(true)
    try {
      const usable = products.filter((p) => (mode === 'upload' ? p.file : p.meshFileId))
      if (!usable.length) { setError(mode === 'upload' ? 'Upload at least one file.' : 'Pick a mesh for at least one product.'); setBusy(false); return }
      const out = []
      for (const p of products) {
        if (mode === 'upload' ? !p.file : !p.meshFileId) { out.push(p); continue }
        const { bytes, filename } = await bytesFor(p)
        const data = await renderReport(bytes, {
          filename, views: [p.view], cornerLabels: false, watermark: false, res: 1400, perspective,
          background: look.background, boneColor: look.boneColor, shadingT: look.shadingT, roughness: look.roughness,
        })
        out.push({ ...p, image: data.views[p.view] })
      }
      setProducts(out)
    } catch (e) { setError(e.response?.data?.error || e.message) } finally { setBusy(false) }
  }

  async function downloadPdf() {
    setBusy(true)
    try {
      const doc = await buildIllustration({
        header, products: products.filter((p) => p.image).map((p) => ({ productName: p.productName, productId: p.productId, image: p.image })),
        logo, logoAspect, appVersion: APP_VERSION,
      })
      doc.save(`${header.caseId || 'illustration'}_illustration.pdf`)
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }

  const rendered = useMemo(() => products.filter((p) => p.image), [products])
  const field = 'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm'
  const lbl = 'block text-xs font-medium text-gray-500 mb-1'
  const fileInput = 'block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-brand-50 file:text-brand-700 file:font-medium'

  return (
    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
      <div className="space-y-4">
        {/* Source — same layout/order as STL/OBJ to PDF */}
        <div className="card space-y-3">
          <div className="flex gap-2">
            <button onClick={() => switchMode('upload')} className={mode === 'upload' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>Upload files</button>
            <button onClick={() => switchMode('case')} className={mode === 'case' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>From case</button>
          </div>
          {mode === 'upload' ? (
            <div>
              <label className={lbl}>STL / OBJ / GLB files <span className="text-gray-400 font-normal">(one page per file)</span></label>
              <input type="file" multiple accept=".stl,.obj,.glb,.gltf" onChange={(e) => onUploadFiles(e.target.files)} className={fileInput} />
              {products.some((p) => p.file) && <p className="text-xs text-green-600 mt-1">{products.filter((p) => p.file).length} file(s) added below ↓</p>}
            </div>
          ) : (
            <div>
              <label className={lbl}>Case ID</label>
              <input value={header.caseId} placeholder="O3D-XXXXXXXX" onChange={(e) => setH('caseId', e.target.value.trim().toUpperCase())} className={field} />
              {meshesLoading ? <p className="text-xs text-gray-400 mt-1">Searching Drive…</p>
                : header.caseId.length >= 8 && meshes.length === 0 ? <p className="text-xs text-amber-600 mt-1">No STL/OBJ found on Drive for this case.</p>
                : meshes.length > 0 ? <p className="text-xs text-green-600 mt-1">{meshes.length} mesh(es) found — add products below ↓</p> : null}
            </div>
          )}
        </div>

        {/* Header info */}
        <div className="card space-y-2">
          <span className="text-sm font-semibold text-gray-700 block">Header info</span>
          <input placeholder="Case ID" value={header.caseId} onChange={(e) => setH('caseId', mode === 'case' ? e.target.value.trim().toUpperCase() : e.target.value)} className={field} />
          <input placeholder="Surgeon name" value={header.surgeonName} onChange={(e) => setH('surgeonName', e.target.value)} className={field} />
          <input placeholder="Hospital name" value={header.hospitalName} onChange={(e) => setH('hospitalName', e.target.value)} className={field} />
          <input placeholder="Patient name" value={header.patientName} onChange={(e) => setH('patientName', e.target.value)} className={field} />
        </div>

        {/* Render look */}
        <div className="card space-y-3">
          <span className="text-sm font-semibold text-gray-700 block">Render look</span>
          <div className="flex items-center gap-2">
            {TONES.map(([label, hex]) => (
              <button key={hex} title={label} onClick={() => setL('boneColor', hex)}
                className={`w-8 h-8 rounded border ${look.boneColor === hex ? 'border-brand-500 ring-1 ring-brand-500' : 'border-gray-300'}`} style={{ backgroundColor: hex }} />
            ))}
            <div className="ml-auto flex gap-1 text-xs">
              {[['White', 'white'], ['Black', 'black']].map(([label, val]) => (
                <button key={val} onClick={() => setL('background', val)} className={`px-2.5 py-1 rounded ${look.background === val ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
              ))}
            </div>
          </div>
          <div><div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Shading</span><span>soft ← → hard</span></div>
            <input type="range" min={0} max={1} step={0.05} value={look.shadingT} onChange={(e) => setL('shadingT', parseFloat(e.target.value))} className="w-full accent-brand-500" /></div>
          <div><div className="flex justify-between text-xs text-gray-500 mb-0.5"><span>Finish</span><span>glossy ← → matte</span></div>
            <input type="range" min={0.3} max={1.0} step={0.02} value={look.roughness} onChange={(e) => setL('roughness', parseFloat(e.target.value))} className="w-full accent-brand-500" /></div>
          <div className="flex items-center justify-between border-t border-gray-100 pt-2">
            <span className="text-xs font-medium text-gray-500">Projection</span>
            <div className="flex gap-1 text-xs">
              {[['Perspective', true], ['Ortho', false]].map(([label, val]) => (
                <button key={label} onClick={() => setPerspective(val)} className={`px-2.5 py-1 rounded ${perspective === val ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Products */}
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Products ({products.length})</span>
            {mode === 'case' && <button className="text-xs text-brand-600" onClick={() => setProducts((ps) => [...ps, emptyProduct()])}>+ Add product</button>}
          </div>
          {products.map((p, i) => (
            <div key={p.id} className="border border-gray-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">Product {i + 1}{mode === 'upload' && p.fileName ? ` · ${p.fileName}` : ''}</span>
                {products.length > 1 && <button className="text-xs text-red-500" onClick={() => setProducts((ps) => ps.filter((_, j) => j !== i))}>Remove</button>}
              </div>
              {mode === 'case' && (
                <select value={p.meshFileId} onChange={(e) => updP(i, { meshFileId: e.target.value })} disabled={!meshes.length} className={`${field} disabled:bg-gray-100`}>
                  <option value="">{meshes.length ? 'Select mesh…' : 'Enter Case ID above'}</option>
                  {meshes.map((m) => <option key={m.id} value={m.id}>{m.name}{m.size ? ` (${(m.size / 1048576).toFixed(0)}MB)` : ''}</option>)}
                </select>
              )}
              <input placeholder="Product name" value={p.productName} onChange={(e) => updP(i, { productName: e.target.value })} className={field} />
              <div className="flex gap-2">
                <input placeholder="Product ID" value={p.productId} onChange={(e) => updP(i, { productId: e.target.value })} className={field} />
                <select value={p.view} onChange={(e) => updP(i, { view: e.target.value })} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                  {VIEWS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                </select>
              </div>
              {p.image && <img src={`data:image/png;base64,${p.image}`} alt="render" className="w-24 rounded border border-gray-100" />}
            </div>
          ))}
          {mode === 'upload' && !products.some((p) => p.file) && <p className="text-xs text-gray-400">Upload files above to add products.</p>}
        </div>

        <button onClick={generate} disabled={busy} className="btn-primary w-full">{busy ? 'Rendering…' : 'Render illustrations'}</button>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      {/* Result */}
      <div className="card min-h-[400px]">
        {rendered.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">{busy ? 'Rendering…' : 'Rendered illustrations will appear here.'}</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-gray-700">Illustrations ({rendered.length} page{rendered.length !== 1 ? 's' : ''})</span>
              <button onClick={downloadPdf} disabled={busy} className="btn-primary">Download PDF</button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {products.filter((p) => p.image).map((p) => (
                <figure key={p.id} className={`text-center border border-gray-100 rounded-lg p-3 ${look.background === 'black' ? 'bg-gray-900' : ''}`}>
                  <img src={`data:image/png;base64,${p.image}`} alt={p.productName} className="w-full rounded" />
                  <figcaption className={`text-xs mt-2 ${look.background === 'black' ? 'text-gray-300' : 'text-gray-600'}`}>{p.productName || '(no name)'} · {p.productId}</figcaption>
                </figure>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
